import { AfterViewInit, ChangeDetectorRef, Component, Inject, Input, NgZone, OnDestroy, OnInit, OnChanges, SimpleChanges, PLATFORM_ID } from '@angular/core';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5timeline from '@amcharts/amcharts5/timeline';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

import { BaseChartService } from '../../../../shared/services/base-chart.service';
import { BarChartData } from '../interfaces/bar-chart-data.interface';
import { CommonService } from '../../../../shared/services/common.service';
/**
 * Spiral Timeline Graph Component
 * @author: PRachethan | 02-08-2025 
 */
@Component({
  selector: 'bi-spiral-timeline-graph',
  standalone: false,
  templateUrl: './spiral-timeline-graph.component.html',
  styleUrl: './spiral-timeline-graph.component.scss'
})
export class SpiralTimelineGraphComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  private root!: am5.Root;
  data: BarChartData[] = [];
  // private _graphsSubscription: Subscription = new Subscription();
  private spiralTimelineGraphReportSubscription: any;
  private isSpiralTimelineGraphViewInitialized = false;


  @Input() reportData: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private _baseChartService: BaseChartService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _commonService: CommonService) { }

  ngOnInit(): void {
    // We need to run amCharts code outside of Angular's zone to prevent change detection issues
    this.zone.runOutsideAngular(() => {
      this.spiralTimelineGraphReportSubscription = this._commonService.graphReportState.subscribe((data: any) => {
        if (data && data.reportData && Array.isArray(data.reportData) && data.reportData.length > 0) {
          this.reportData = data;
          console.log("Spiral Timeline Graph Data", this.reportData);
          this.data = this._baseChartService.transformReportData(data);
          if (this.isSpiralTimelineGraphViewInitialized) {
            this.updateChart(this.data);
          }
        } else {
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.isSpiralTimelineGraphViewInitialized = true;
    if (this.reportData && this.reportData.reportData && Array.isArray(this.reportData.reportData) && this.reportData.reportData.length > 0) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportData'] && this.reportData?.reportData?.length > 0 && this.isSpiralTimelineGraphViewInitialized) {
      this.zone.runOutsideAngular(() => {
        this.data = this._baseChartService.transformReportData(this.reportData);
        this.updateChart(this.data);
      });
    }
  }

  private validateAndCleanData(data: BarChartData[]): BarChartData[] {
    if (!data || data.length === 0) return [];
    const categoryField = this._baseChartService.getCategoryField(data);
    return data.map((item) => {
      const cleaned: BarChartData = { ...item };
      const cat = cleaned[categoryField];
      if (cat !== undefined && cat !== null && typeof cat === 'object') {
        const obj = cat as any;
        cleaned[categoryField] = (obj?.col_value != null ? String(obj.col_value) : JSON.stringify(obj)).trim();
      } else if (typeof cat === 'string') {
        (cleaned as any)[categoryField] = cat.trim();
      }
      return cleaned;
    });
  }

  createChart(data: BarChartData[]) {
    const cleanedData = this.validateAndCleanData(data);
    if (cleanedData.length === 0) {
      return;
    }

    this.root = this._baseChartService.createRoot('spiral-timeline-chartdiv');
    this._baseChartService.attachExporting(this.root, {
      filePrefix: 'spiral-timeline',
      data: cleanedData
    });

    this.root.setThemes([am5themes_Animated.new(this.root)]);

    const wrapper = this.root.container.children.push(am5.Container.new(this.root, {
      width: am5.percent(100),
      height: am5.percent(100),
      layout: this.root.verticalLayout
    }));
    const chartHost = wrapper.children.push(am5.Container.new(this.root, {
      width: am5.percent(100),
      height: am5.percent(82)
    }));

    const chart = chartHost.children.push(am5timeline.SpiralChart.new(this.root, {
      width: am5.percent(100),
      height: am5.percent(100),
      levelCount: 2,
      wheelY: "zoomX"
    }));

    this._baseChartService.setupChartAnimation(chart, 'slide');
    this._baseChartService.setupColorPalette(chart);

    const pathColors = [
      am5.color(0x16a34a),
      am5.color(0xdc2626),
      am5.color(0x0ea5e9),
      am5.color(0xca8a04),
      am5.color(0x9333ea),
      am5.color(0xec4899)
    ];

    chart.set("scrollbarX", am5.Scrollbar.new(this.root, {
      orientation: "horizontal"
    }));

    const yRenderer = am5timeline.AxisRendererCurveY.new(this.root, {});
    yRenderer.labels.template.setAll({
      centerY: am5.p50,
      centerX: am5.p100,
      fontSize: 11
    });
    yRenderer.grid.template.set("forceHidden", true);

    const xRenderer = am5timeline.AxisRendererCurveX.new(this.root, {
      yRenderer: yRenderer,
      stroke: am5.color(0x9ca3af),
      strokeOpacity: 0.9
    });

    xRenderer.grid.template.set('strokeWidth', 1.5);
    xRenderer.grid.template.set('stroke', am5.color(0x9ca3af));
    xRenderer.grid.template.set('strokeOpacity', 0.7);
    xRenderer.grid.template.set('strokeDasharray', [4, 4]);

    xRenderer.labels.template.setAll({
      centerY: am5.p50,
      fontSize: 10,
      minPosition: 0.01,
      fill: am5.color(0x6b7280)
    });
    xRenderer.labels.template.setup = (target: any) => {
      target.set("layer", 30);
      target.set("background", am5.Rectangle.new(this.root, {
        fill: am5.color(0xffffff),
        fillOpacity: 1
      }));
    };

    const categoryField = this._baseChartService.getCategoryField(cleanedData);
    const valueField = this._baseChartService.getValueField(cleanedData);

    const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(this.root, {
      maxDeviation: 0,
      categoryField: "category",
      renderer: yRenderer
    }) as any);

    this._baseChartService.setupAxisAnimation(yAxis);

    const colorSet = chart.get("colors") as am5.ColorSet;
    const timelineData = this.buildTimelineDataFromReport(cleanedData, categoryField, valueField, colorSet);

    const uniqueCategories = [...new Set(timelineData.map((d: any) => d.category).filter((c: string) => c != null && c !== ''))];
    const categoryToColorIndex: Record<string, number> = {};
    uniqueCategories.forEach((cat: string, i: number) => {
      categoryToColorIndex[cat] = i % pathColors.length;
    });

    const xMax = timelineData.length > 0
      ? Math.ceil(Math.max(...timelineData.map((d: any) => d.end))) + 2
      : 12;

    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(this.root, {
      renderer: xRenderer,
      tooltip: am5.Tooltip.new(this.root, {}),
      min: 0,
      max: xMax,
      strictMinMax: false
    }) as any);

    this._baseChartService.setupAxisAnimation(xAxis);

    const series = chart.series.push(am5timeline.CurveColumnSeries.new(this.root, {
      xAxis: xAxis,
      yAxis: yAxis,
      baseAxis: yAxis,
      valueXField: "end",
      openValueXField: "start",
      categoryYField: "category",
      layer: 30
    }));

    this._baseChartService.setupSeriesAnimation(series, 'elastic');

    series.columns.template.setAll({
      height: am5.percent(10),
      strokeOpacity: 0,
      fillOpacity: 1
    });

    series.columns.template.adapters.add('fill', (fill: any, target: any) => {
      const category = target.dataItem?.dataContext?.category;
      const idx = category != null ? (categoryToColorIndex[category] ?? 0) : 0;
      return pathColors[idx % pathColors.length];
    });

    series.bullets.push((root: am5.Root, s: any, dataItem: any) => {
      const category = dataItem?.dataContext?.category;
      const idx = category != null ? (categoryToColorIndex[category] ?? 0) : 0;
      const color = pathColors[idx % pathColors.length];
      const circle = am5.Circle.new(root, {
        radius: 5,
        fill: color,
        fillOpacity: 1,
        stroke: color,
        strokeWidth: 2,
        strokeOpacity: 1,
        layer: 30
      });
      return am5.Bullet.new(root, {
        sprite: circle,
        locationX: 1,
        locationY: 0.5
      });
    });

    series.bullets.push(() => {
      const label = am5.Label.new(this.root, {
        text: '{category}',
        populateText: true,
        centerX: am5.p50,
        centerY: am5.p50,
        fontSize: 10,
        fill: am5.color(0x333333),
        maxWidth: 150,
        oversizedBehavior: 'wrap',
        breakWords: true,
        dx: 16
      });
      return am5.Bullet.new(this.root, {
        sprite: label,
        locationX: 1,
        locationY: 0.5
      });
    });

    const tooltip = am5.Tooltip.new(this.root, {
      labelText: "{category}: {task}"
    });
    this._baseChartService.setupTooltipAnimation(tooltip);
    series.set('tooltip', tooltip);

    series.set('maskBullets', false);
    series.data.setAll(timelineData);

    const categories = [...new Set(timelineData.map((d: any) => d.category).filter((c: string) => c != null && c !== ''))];
    yAxis.data.setAll(categories.map((category: string) => ({ category })));

    chart.set("cursor", am5timeline.CurveCursor.new(this.root, {
      behavior: "zoomX",
      xAxis: xAxis,
      yAxis: yAxis
    }));

    const legend = wrapper.children.push(
      this._baseChartService.createLegend(this.root)
    );
    legend.setAll({
      width: am5.percent(100),
      centerX: am5.p50,
      x: am5.p50,
      marginTop: 8,
      marginBottom: 8
    });
    legend.markers.template.setAll({ width: 12, height: 12 });
    legend.labels.template.setAll({
      text: '{categoryY}',
      fontSize: 12,
      oversizedBehavior: 'truncate',
      maxWidth: 180
    });
    legend.valueLabels.template.set('forceHidden', true);
    legend.markerRectangles.template.adapters.add('fill', (fill, target) => {
      const dataItem = target.dataItem;
      if (!dataItem) {
        return fill;
      }
      const index = series.dataItems.indexOf(dataItem);
      return index >= 0 ? colorSet.getIndex(index) : fill;
    });
    legend.markerRectangles.template.adapters.add('stroke', (stroke, target) => {
      const dataItem = target.dataItem;
      if (!dataItem) {
        return stroke;
      }
      const index = series.dataItems.indexOf(dataItem);
      return index >= 0 ? colorSet.getIndex(index) : stroke;
    });
    const updateLegend = () => {
      if (series.dataItems?.length > 0) {
        legend.data.setAll(series.dataItems);
      }
    };
    series.events.on('datavalidated', updateLegend);
    updateLegend();
    this._baseChartService.setupLegendAnimation(legend);

    setTimeout(() => {
      series.appear(1000);
      chart.appear(1000, 100);
    }, 100);
  }

  /**
   * Build timeline data from our report data only (no sample data).
   * Shape: category, start, end, task, color (like amCharts sample structure).
   */
  private buildTimelineDataFromReport(
    data: BarChartData[],
    categoryField: string,
    valueField: string,
    colorSet: am5.ColorSet
  ): any[] {
    const out: any[] = [];
    let position = 0;
    data.forEach((item, index) => {
      const category = item[categoryField];
      if (category === undefined || category === null || (typeof category === 'string' && !category.trim())) {
        return;
      }
      const categoryStr = typeof category === 'string' ? category.trim() : String(category);
      const raw = item[valueField];
      const value = (raw !== undefined && raw !== null && typeof raw === 'number' && !Number.isNaN(raw))
        ? Number(raw)
        : 0;
      const segmentLength = Math.max(0.5, Math.min(Math.abs(value), 20));
      const start = position;
      const end = position + segmentLength;
      position = end + 0.5;
      out.push({
        category: categoryStr,
        start,
        end,
        task: valueField,
        value,
        color: colorSet.getIndex(index)
      });
    });
    return out;
  }

  private updateChart(data: BarChartData[]) {
    if (this.root) {
      this._baseChartService.disposeChart(this.root);
    }
    this.createChart(data);
    
    // Update the chart data in the registry
    BaseChartService.updateActiveChartData(data);
  }

  ngOnDestroy(): void {
    this._baseChartService.disposeChart(this.root);
    if (this.spiralTimelineGraphReportSubscription) {
      this.spiralTimelineGraphReportSubscription.unsubscribe();
    }
  }
}
